import React, { useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { visuallyHidden } from "@mui/utils";
import PropTypes from "prop-types";

import {
  Box,
  Button,
  Chip,
  IconButton,
  TablePagination,
  TableSortLabel,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  UilPlus,
  UilInfoCircle,
  UilPen,
  UilPadlock,
  UilLockOpenAlt,
  UilKeySkeletonAlt,
} from "@iconscout/react-unicons";
import Color from "../../enum/Color";
import UserStatusEnum from "../../enum/UserStatusEnum";
import EmploymentTypeEnum from "../../enum/EmploymentTypeEnum";
import { baseUrl } from "../../Config";
import { SendGetRequest } from "../../actions/Actions";
import { useContext } from "react";
import AuthContext from "../../store/auth-context";
import BackdropContext from "../../store/backdrop-context";
import UserModal from "../Modal/UserModal";
import CreateButton from "../Common/CreateButton";

const statusStyle = (status) => {
  if (status === UserStatusEnum.ACTIVE) {
    return {
      backgroundColor: Color.SUCCESS,
      color: Color.FOREGROUND,
    };
  } else if (status === UserStatusEnum.DISABLED) {
    return {
      backgroundColor: Color.DANGER,
      color: Color.FOREGROUND,
    };
  }
};

const typeStyle = (status) => {
  if (status === EmploymentTypeEnum.INTERNAL) {
    return {
      backgroundColor: Color.SUCCESS,
      color: Color.FOREGROUND,
    };
  } else if (status === EmploymentTypeEnum.EXTERNAL) {
    return {
      backgroundColor: Color.WARNING,
      color: Color.FOREGROUND,
    };
  } else {
    return {
      backgroundColor: Color.BACKGROUND,
      color: Color.FOREGROUND,
    };
  }
};

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  {
    id: "firstName",
    numeric: false,
    disablePadding: false,
    label: "First Name",
  },
  {
    id: "lastName",
    numeric: false,
    disablePadding: false,
    label: "Last Name",
  },
  {
    id: "email",
    numeric: false,
    disablePadding: false,
    label: "Email",
  },
  {
    id: "status",
    numeric: false,
    disablePadding: false,
    label: "Status",
  },
  {
    id: "type",
    numeric: false,
    disablePadding: false,
    label: "Type",
  },
  {
    id: "primaryActions",
    numeric: false,
    disablePadding: false,
    label: "",
  },
  {
    id: "secondaryActions",
    numeric: false,
    disablePadding: false,
    label: "",
  },
];

function EnhancedTableHead(props) {
  const { order, orderBy, rowCount, onRequestSort } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={"left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

const EnhancedTableToolbar = () => {
  return (
    <Toolbar
      sx={{
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
      }}
    >
      <Typography
        sx={{ flex: "1 1 100%" }}
        variant="h6"
        id="tableTitle"
        component="div"
      >
        Customers
      </Typography>
    </Toolbar>
  );
};

const UsersTable = (props) => {
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState("");
  const [isReadOnly, setIsReadOnly] = useState(false);

  const authContext = useContext(AuthContext);
  const backdropContext = useContext(BackdropContext);

  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("id");
  const [page, setPage] = useState(0);
  const [dense, setDense] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const tableData = props.data;
  const roles = props.roles;

  const closeModalHandler = () => {
    setShowModal((prev) => {
      return !prev;
    });

    props.refreshHandler();
  };

  const showDetails = (rowData, isReadOnly) => {
    setShowModal((prev) => {
      return !prev;
    });
    setModalData(() => {
      return rowData;
    });
    setIsReadOnly(isReadOnly);
  };

  const newModal = () => {
    setModalData(null);
    setShowModal((prev) => {
      return !prev;
    });
  };

  const lockHandler = (rowData) => {
    const url =
      baseUrl + "/api/user/change-status?username=" + rowData.username;
    SendGetRequest(url, authContext.token, actionsHandler);
    backdropContext.toggle();
  };

  const resetHandler = (rowData) => {
    const url =
      baseUrl + "/api/user/resend-password?username=" + rowData.username;
    SendGetRequest(url, authContext.token, actionsHandler);
    backdropContext.toggle();
  };

  const actionsHandler = () => {
    backdropContext.toggle();
    props.refreshHandler();
  }

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - tableData.length) : 0;

  return (
    <div className="Table">
      <UserModal
        isVisible={showModal}
        isReadOnly={isReadOnly}
        closeModalHandler={closeModalHandler}
        modalData={modalData}
        roles={roles}
      />

      <CreateButton action={newModal} icon={<UilPlus />} text="New User" />
      <Box sx={{ width: "100%" }}>
        <Paper sx={{ width: "100%", mb: 2 }}>
          <EnhancedTableToolbar />
          <TableContainer>
            <Table
              sx={{ minWidth: 750 }}
              aria-labelledby="tableTitle"
              size={dense ? "small" : "medium"}
            >
              <EnhancedTableHead
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
                rowCount={tableData.length}
              />
              <TableBody>
                {/* if you don't need to support IE11, you can replace the `stableSort` call with:
                 rows.slice().sort(getComparator(order, orderBy)) */}
                {stableSort(tableData, getComparator(order, orderBy))
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, index) => {
                    const labelId = `enhanced-table-checkbox-${index}`;

                    return (
                      <TableRow hover tabIndex={-1} key={row.id}>
                        <TableCell align="left">{row.firstName}</TableCell>
                        <TableCell align="left">{row.lastName}</TableCell>
                        <TableCell align="left">{row.email}</TableCell>
                        <TableCell align="left">
                          {
                            <Chip
                              sx={{ minWidth: "80px" }}
                              label={row.userStatus}
                              style={statusStyle(row.userStatus)}
                            />
                          }
                        </TableCell>
                        <TableCell align="left">
                          {
                            <Chip
                              sx={{ minWidth: "80px" }}
                              label={row.employmentType}
                              style={typeStyle(row.employmentType)}
                            />
                          }
                        </TableCell>
                        <TableCell align={"left"} className="Details">
                          <Tooltip title="Customer info">
                            <IconButton onClick={() => showDetails(row, true)}>
                              <UilInfoCircle
                                style={{ color: Color.BACKGROUND }}
                              />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit customer">
                            <IconButton onClick={() => showDetails(row, false)}>
                              <UilPen style={{ color: Color.BACKGROUND }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                        <TableCell align={"left"} className="Details">
                          <Tooltip title={"Reset and resend user credentials"}>
                            <IconButton onClick={() => resetHandler(row)}>
                              <UilKeySkeletonAlt
                                style={{ color: Color.WARNING }}
                              />
                            </IconButton>
                          </Tooltip>
                          <Tooltip
                            title={
                              row.userStatus === UserStatusEnum.ACTIVE
                                ? "Deactivate user"
                                : "Activate user"
                            }
                          >
                            <IconButton onClick={() => lockHandler(row)}>
                              {row.userStatus === UserStatusEnum.ACTIVE ? (
                                <UilPadlock style={{ color: Color.DANGER }} />
                              ) : (
                                <UilLockOpenAlt
                                  style={{ color: Color.SUCCESS }}
                                />
                              )}
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                {emptyRows > 0 && (
                  <TableRow
                    style={{
                      height: (dense ? 33 : 53) * emptyRows,
                    }}
                  >
                    <TableCell colSpan={6} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={tableData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </Box>
    </div>
  );
};

export default UsersTable;
